import Loading from 'ui/atoms/Loading/Loading';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Icon from 'core/modules/atoms/Icon';
import './_topic-ranking-detail.scss';
import useTopicDetail from './useTopicDetail';

type TopicRankingDetailProps = {
  colorIndex: number;
  courseIds: string[];
  endDate: string | undefined;
  frequency: number;
  isExpanded: boolean;
  startDate: string | undefined;
  topicId: string | null;
};

function TopicRankingDetail({ colorIndex, courseIds, endDate, frequency, isExpanded, startDate, topicId }: TopicRankingDetailProps): JSX.Element | null {
  const { t } = useTranslation(['chatbot']);
  const { error, loading, users } = useTopicDetail({ courseIds, endDate, isExpanded, startDate, topicId });
  const colorNumber = (colorIndex % 8) + 1;

  const maxUserFrequency = useMemo(() => {
    if (users.length === 0) return 1;
    return Math.max(...users.map((u) => u.frequency));
  }, [users]);

  const totalExtendedSearch = useMemo(() => {
    return users.reduce((sum, u) => sum + u.extendedSearchCount, 0);
  }, [users]);

  if (!isExpanded) return null;

  if (loading) {
    return (
      <div className="topic-ranking-detail__loading">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="topic-ranking-detail__error">
        <p>{t('chatbot:ai_analytics_topic_ranking_detail_error')}</p>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="topic-ranking-detail__empty">
        <p>{t('chatbot:ai_analytics_topic_ranking_detail_no_students')}</p>
      </div>
    );
  }

  return (
    <div className={`topic-ranking-detail topic-ranking-detail--color-${colorNumber}`}>
      <div className="topic-ranking-detail__summary">
        <div className="topic-ranking-detail__summary-item">
          <Icon type="students" size="default" />
          <span>{t('chatbot:ai_analytics_topic_ranking_detail_unique_students', { count: users.length })}</span>
        </div>
        <div className="topic-ranking-detail__summary-item">
          <Icon type="book-open" size="default" />
          <span>{t('chatbot:ai_analytics_topic_ranking_detail_total_queries', { count: frequency })}</span>
        </div>
        {totalExtendedSearch > 0 && (
          <div className="topic-ranking-detail__summary-item">
            <Icon type="search" size="default" />
            <span>{t('chatbot:ai_analytics_topic_ranking_detail_extended_search_summary', { count: totalExtendedSearch, total: frequency })}</span>
          </div>
        )}
      </div>

      <div className="topic-ranking-detail__table">
        <div className="topic-ranking-detail__table-header">
          <span className="topic-ranking-detail__table-header-label topic-ranking-detail__table-header-label--student">
            {t('chatbot:ai_analytics_topic_ranking_detail_student')}
          </span>
          <span className="topic-ranking-detail__table-header-label topic-ranking-detail__table-header-label--queries">
            {t('chatbot:ai_analytics_topic_ranking_detail_queries')}
          </span>
        </div>
        <div className="topic-ranking-detail__table-body">
          {users.map((user) => {
            const barPercent = maxUserFrequency > 0 ? (user.frequency / maxUserFrequency) * 100 : 0;
            return (
              <div className="topic-ranking-detail__user-row" key={user.userId}>
                <div className="topic-ranking-detail__user-info">
                  <span className="topic-ranking-detail__user-name">
                    {user.name} {user.lastName}
                  </span>
                  {user.extendedSearchCount > 0 && (
                    <span className="topic-ranking-detail__extended-badge">
                      <Icon type="search" size="default" />
                      {t('chatbot:ai_analytics_topic_ranking_detail_extended_badge', { count: user.extendedSearchCount, total: user.frequency })}
                    </span>
                  )}
                </div>
                <div className="topic-ranking-detail__user-queries">
                  <div className="topic-ranking-detail__user-bar">
                    <div className="topic-ranking-detail__user-bar-fill" style={{ width: `${barPercent}%` }} />
                  </div>
                  <span className="topic-ranking-detail__user-frequency">{user.frequency}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default TopicRankingDetail;
